import { BinanceWalletConnector } from '@pancakeswap/wagmi/connectors/binanceWallet'
import { BloctoConnector } from '@pancakeswap/wagmi/connectors/blocto'
import { TrustWalletConnector } from '@pancakeswap/wagmi/connectors/trustWallet'
import { bsc, bscTestnet, goerli, mainnet } from 'wagmi/chains'
import { configureChains, createClient } from 'wagmi'
import memoize from 'lodash/memoize'
import { CoinbaseWalletConnector } from 'wagmi/connectors/coinbaseWallet'
import { InjectedConnector } from 'wagmi/connectors/injected'
import { MetaMaskConnector } from 'wagmi/connectors/metaMask'
import { WalletConnectConnector } from 'wagmi/connectors/walletConnect'
import { LedgerConnector } from 'wagmi/connectors/ledger'
import { jsonRpcProvider } from 'wagmi/providers/jsonRpc'
import { SafeConnector } from './safeConnector'

const CHAINS = [bsc, mainnet, bscTestnet, goerli]


const getNodeRealUrl = (networkName: string, isProxy = false, isSecond = false) => {
  let host = null

  switch (networkName) {
    case 'homestead':
      host = `eth-mainnet.nodereal.io/v1/${isSecond ? process.env.NEXT_PUBLIC_NODE_REAL_API_ETH2 : process.env.NEXT_PUBLIC_NODE_REAL_API_ETH}`
      break
    case 'bsc':
      host = `bsc-mainnet.nodereal.io/v1/${isSecond ? process.env.NEXT_PUBLIC_NODE_REAL_API_BSC2 : process.env.NEXT_PUBLIC_NODE_REAL_API_BSC}`
      break
    default:
      host = null
  }

  if (!host) {
    return null
  }

  const url = `https://${host}`
  return {
    http: isProxy ? `/api/node/${networkName}` : url,
    webSocket: url.replace(/^http/i, 'wss').replace('.nodereal.io/v1', '.nodereal.io/ws/v1'),
  }
}

export const { provider, chains } = configureChains(CHAINS, [
  jsonRpcProvider({
    rpc: chain => {
      if (process.env.NODE_ENV === 'production' && chain.id === bsc.id) {
        return {http: 'https://rpc.muskswap.club/bsc'}
      }

      if (process.env.NODE_ENV === 'production' && chain.id === mainnet.id) {
        return {http: 'https://rpc.muskswap.club/eth'}
      }

      return {http: chain.rpcUrls.default.http[0]}
    }
  }),
  jsonRpcProvider({
    rpc: (chain) => {
      if (process.env.NODE_ENV !== 'production' && chain.id === bsc.id) {
        return {http: 'https://bsc-dataseed.binance.org'}
      }

      if (process.env.NODE_ENV !== 'production' && chain.id === mainnet.id) {
        return {http: 'https://cloudflare-eth.com'}
      }

      return getNodeRealUrl(chain.network, false, true) || {http: chain.rpcUrls.default.http[0]}
    },
  }),
  jsonRpcProvider({
    rpc: (chain) => {
      if (process.env.NODE_ENV !== 'production' && chain.id === bsc.id) {
        return {http: 'https://bsc-dataseed.binance.org'}
      }

      if (process.env.NODE_ENV !== 'production' && chain.id === mainnet.id) {
        return {http: 'https://cloudflare-eth.com'}
      }

      return getNodeRealUrl(chain.network, false, false) || {http: chain.rpcUrls.default.http[0]}
    },
  }),
  jsonRpcProvider({
    rpc: chain => ({http: chain.rpcUrls.default.http[0]})
  }),
  jsonRpcProvider({
    rpc: (chain) => {
      if (!!process.env.NEXT_PUBLIC_NODE_PRODUCTION && chain.id === bsc.id) {
        return { http: process.env.NEXT_PUBLIC_NODE_PRODUCTION }
      }
      if (process.env.NODE_ENV === 'test' && chain.id === mainnet.id) {
        return { http: 'https://cloudflare-eth.com' }
      }

      return getNodeRealUrl(chain.network) || { http: chain.rpcUrls.default.http[0] }
    },
  }),
])

export const injectedConnector = new InjectedConnector({
  chains,
  options: {
    shimDisconnect: false,
    shimChainChangedDisconnect: true,
  },
})

export const coinbaseConnector = new CoinbaseWalletConnector({
  chains,
  options: {
    appName: 'PancakeSwap',
    appLogoUrl: 'https://pancakeswap.com/logo.png',
  },
})

export const walletConnectConnector = new WalletConnectConnector({
  chains,
  options: {
    qrcode: true,
  },
})

export const walletConnectNoQrCodeConnector = new WalletConnectConnector({
  chains,
  options: {
    qrcode: false,
  },
})

export const metaMaskConnector = new MetaMaskConnector({
  chains,
  options: {
    shimDisconnect: false,
    shimChainChangedDisconnect: true,
  },
})

const bloctoConnector = new BloctoConnector({
  chains,
  options: {
    defaultChainId: 56,
    appId: 'e2f2f0cd-3ceb-4dec-b293-bb555f2ed5af',
  },
})

const ledgerConnector = new LedgerConnector({
  chains,
})

export const bscConnector = new BinanceWalletConnector({ chains })

export const trustWalletConnector = new TrustWalletConnector({
  chains,
  options: {
    shimDisconnect: false,
    shimChainChangedDisconnect: true,
  },
})

export const client = createClient({
  autoConnect: false,
  provider,
  connectors: [
    new SafeConnector({ chains }),
    metaMaskConnector,
    injectedConnector,
    coinbaseConnector,
    walletConnectConnector,
    bscConnector,
    bloctoConnector,
    ledgerConnector,
    trustWalletConnector,
  ],
})

export const CHAIN_IDS = chains.map((c) => c.id)

export const isChainSupported = memoize((chainId: number) => CHAIN_IDS.includes(chainId))
export const isChainTestnet = memoize((chainId: number) => chains.find((c) => c.id === chainId)?.testnet)
